import {Device} from '@twilio/voice-sdk';
import {Dispatch, SetStateAction} from 'react';
import {SocketLikeWithNamespace} from 'socket.io-react-hook/lib/cjs/types';

import {VoiceInputControls} from 'components';
import {VideoPinParticipant, WebRTCServerSource} from 'enums';
import {
    AsyncRequestStatusName, AsyncRequestTypeName, ClientSession, ParticipantAsyncRequest, ScreenSharingActivityStatus,
    SessionActivityTextChatDataState, SessionFeedback, ClientDevice
} from 'interfaces';
import {
    ClientSessionParams, ClientSettings, WebRTCConnectDetails,
    TextChatThreads, RequestAppointmentDetails, SessionActivityTextChatInteractiveChatState, TextChatMessage
} from 'types';

export type AssignmentStatePayload = {
    agentsAvailable: boolean;
};

export type ClientSessionResponsePayload = ClientSessionStatePayload & {
    token: string;
    assignmentState: AssignmentStatePayload;
    session: ClientSession;
    settings: ClientSettings;
    disabled?: boolean;
}

export type ClientSessionStatePayload = {
    session: ClientSession;
    settings?: ClientSettings;
}

export type SessionActionsContainerContextProps = {
    currentView: ClientView;
    isDevelopmentMode: boolean;
    conversationOnly: boolean;
    errorMessageTKey: string;
    waitingStartSessionFromChildren: boolean;
    device: ClientDevice;
    features: ClientAppearanceFeatures;
    startSessionWithParams: (clientSessionParams: ClientSessionParams) => void;
    startSessionConnect: (token: string) => void;
};

export type SessionActionsContextProps = {
    socket: SocketLikeWithNamespace;
    audioEnabled: boolean;
    videoEnabled: boolean;
    fullScreenVideo: boolean;
    videoDevices: MediaDeviceInfo[];
    audioDevices: MediaDeviceInfo[];
    videoConstraints: MediaTrackConstraintSet;
    textChatThreads: TextChatThreads; // TODO: make sure it will work
    avatarThreads: TextChatThreads; // TODO: make sure it will work
    textChatInteractiveChatState: SessionActivityTextChatInteractiveChatState;
    participantsNetworkQuality: {[participantId: string]: number};
    untreatedAsyncRequests: ParticipantAsyncRequest[];
    twilioDevice: Device;
    tracks: MediaStreamTrack[];
    // Push-to-talk in the text chat: hold the microphone button to speak instead of typing.
    voiceInput?: VoiceInputControls;
    // Freezes the text chat input while the chatbot is ending the session.
    textChatInputDisabled?: boolean;
    preSessionFormDrafts?: React.MutableRefObject<Record<string, {
        values: Record<string, string>;
        initialValues: Record<string, string>;
    }>>;
    videoPinParticipant: VideoPinParticipant;
    pinnedParticipantId?: string;
    setTreatedAsyncRequest: (type: AsyncRequestTypeName) => void;
    onToggleAudio: () => void;
    onToggleVideo: () => void;
    setScreenSharingActivityStatus: (status: ScreenSharingActivityStatus) => void;
    setVideoConstraints: (settings: MediaTrackConstraintSet) => void;
    onToggleFullScreenVideo: (value?: boolean) => void;
    onLocalNetworkQualityLevelChange: (level: number) => void;
    onRemoteNetworkQualityLevelChange: (level: number) => void;
    setMediaDevices: (devices: MediaDeviceInfo[]) => void;
    setTracks: (tracks: MediaStreamTrack[]) => void;
    notifyWebRTCStartSuccess: (source: WebRTCServerSource, connectStatus?: WebRTCConnectDetails) => void;
    notifyWebRTCStartFailed: (source: WebRTCServerSource, error: WebRTCConnectDetails) => void;
    endLocalSession: () => void;
    handleUploadSelfieVerificationResult: (success: boolean) => void;
    executeUploadSelfieVerification: () => void;
    addFeedback: (feedback: SessionFeedback) => Promise<void>;
    handleVideoPinParticipantUpdate: (participant: VideoPinParticipant, participantId?: string) => void;
    requestAppointment: (data: RequestAppointmentDetails) => Promise<void>;
    updateParticipantAsyncRequest: (requestType: AsyncRequestTypeName, status: AsyncRequestStatusName, data: any) => Promise<any>
    onFileDownload: (fileName: string, activityId: string) => Promise<any>;
    onSubmitMessage: (message: Partial<TextChatMessage>) => Promise<any>;
    onChangeTextChatActivityState?: (value: SessionActivityTextChatDataState) => void;
    onTogglePictureInPicture: () => void;
    setTwilioDevice: Dispatch<SetStateAction<Device>>;
    initTwilioDevice: () => void;
}

export type ClientContextProps = ClientSessionResponsePayload & SessionActionsContextProps & SessionActionsContainerContextProps;

export enum ClientView {
    FullScreenTextChat = 'FullScreenTextChat',
    Calling = 'Calling',
    WaitingForAgent = 'WaitingForAgent',
    SIP = 'sip',
    InSession = 'InSession',
    OnHold = 'OnHold',
    SessionEnded = 'SessionEnded',
    AgentsUnavailable = 'AgentsUnavailable',
    AfterHours = 'AfterHours',
    RequestAppointment = 'RequestAppointment',
    Loading = 'Loading',
    Avatar = 'Avatar',
    FullScreenAsyncRequest = 'FullScreenAsyncRequest',
    PreSessionFlow = 'PreSessionFlow',
}

export enum ClientAppearanceFeatures {
    All = 'all',
    Video = 'video'
}

export enum SessionInterfaceEvent {
    OpenSession = 'open-session',
    EndSession = 'end-session',
    SelfieVerificationResult = 'selfie-verification-result'
}